import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import dayjs, { ISO_DATE_FORMAT } from "../dayjs";

interface BaseFilters {
  query: string;
}

export interface ProductFilters extends BaseFilters {
  providerId: string | undefined;
}

export interface CustomerNotesFilters {
  dateFrom: string;
  dateTo: string;
  accountManagerId: string | null;
  authorId: string | null;
  type: string | null;
}

export interface AgentCallsFilters {
  dateFrom: string;
  dateTo: string;
  agentId: string | null;
}

export interface Filters {
  agents: BaseFilters;
  providers: BaseFilters;
  products: ProductFilters;
  packages: BaseFilters;
  loyalty: BaseFilters;
  rewards: BaseFilters;
  "customer-notes": CustomerNotesFilters;
  "phone-calls": AgentCallsFilters;
}

export type ChangeFilters = ((
  key: "agents" | "providers" | "packages" | "loyalty" | "rewards",
  value: BaseFilters,
) => void) &
  ((key: "products", value: ProductFilters) => void) &
  ((key: "customer-notes", value: CustomerNotesFilters) => void) &
  ((key: "phone-calls", value: AgentCallsFilters) => void);

const defaultCustomerNotesFilters = () => ({
  dateFrom: dayjs().subtract(7, "day").format(ISO_DATE_FORMAT),
  dateTo: dayjs().format(ISO_DATE_FORMAT),
  accountManagerId: null,
  authorId: null,
  type: null,
});

export function defaultAgentCallsFilters(): AgentCallsFilters {
  return {
    dateFrom: dayjs().subtract(7, "day").format(ISO_DATE_FORMAT),
    dateTo: dayjs().format(ISO_DATE_FORMAT),
    agentId: null,
  };
}

export function useAdminFilters() {
  const [filters, setFilters] = useState<Filters>({
    agents: { query: "" },
    providers: { query: "" },
    products: { query: "", providerId: undefined },
    packages: { query: "" },
    loyalty: { query: "" },
    rewards: { query: "" },
    "customer-notes": defaultCustomerNotesFilters(),
    "phone-calls": defaultAgentCallsFilters(),
  });

  const handleFiltersChange: ChangeFilters = (key, value) =>
    setFilters((prevFilters) => ({ ...prevFilters, [key]: value }));

  return { filters, handleFiltersChange };
}

export function useAdminFiltersValue<T extends keyof Filters>(filterType: T): Filters[T] {
  const context = useOutletContext<Filters>();
  if (context) {
    const filters = context[filterType];
    if ("query" in filters) {
      return { ...filters, query: filters.query.toLowerCase() } as Filters[T];
    }
    return filters;
  }
  if (filterType === "customer-notes") {
    return defaultCustomerNotesFilters() as Filters[T];
  }
  if (filterType === "phone-calls") {
    return defaultAgentCallsFilters() as Filters[T];
  }
  return { query: "", providerId: undefined } as Filters[T];
}
